import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import userAgreementsClient from '@modules/clients/userAgreementsClient';
import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import DocsAssistantEvent from '@modules/eventStream/enum/DocsAssistantEvent';
import { useLocalization } from '@rbx/intl';
import throwError from '@modules/utilities/error';

// check of the state workflow in the PR description
// https://github.rbx.com/Roblox/creator-doc-site/pull/532
export enum OptInState {
  Preinitialized = 'preinitialized',
  Initialized = 'initialized',
  InitializedWithSentPrompt = 'initializedWithSentPrompt',
  FailedToInitialize = 'failedToInitialize',
  WaitForInput = 'waitForInput',
  SentPromptPending = 'sentPromptPending',
  AcceptedLoading = 'acceptedLoading',
  FailedToAccept = 'failedToAccept',
  DeclinedLoading = 'declinedLoading',
  AcceptedWithoutProcess = 'acceptedWithoutProcess',
  Accepted = 'accepted',
  Declined = 'declined',
}

type ProcessCallback = () => void;
export type AssistantOptInType = {
  state: OptInState;
  processing: boolean;
  accept: () => void;
  decline: () => void;
  process: (cb: ProcessCallback) => void;
  initialize: () => void;
  sentPromptPending: () => void;
  accepted: boolean;
  initialized: boolean;
};
const AssistantOptInContext = createContext<AssistantOptInType>({
  state: OptInState.Preinitialized,
  processing: false,
  accept: () => {
    throw Error('not implemented');
  },
  decline: () => {
    throw Error('not implemented');
  },
  process: () => {
    throw Error('not implemented');
  },
  initialize: () => {
    throw Error('not implemented');
  },
  sentPromptPending: () => {
    throw Error('not implemented');
  },
  accepted: false,
  initialized: false,
});
AssistantOptInContext.displayName = 'AssistantOptIn';

export const AssistantOptInProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { locale } = useLocalization();
  const [state, setState] = useState(OptInState.Preinitialized);
  const [agreementIds, setAgreementIds] = useState<string[]>([]);
  const processCallback = useRef<ProcessCallback | null>(null);

  // Ref to hold the latest state to avoid unnecessary re-renders in processOptIn
  const stateRef = useRef(state);

  useEffect(() => {
    stateRef.current = state;
    if (state === OptInState.Accepted && processCallback.current) {
      processCallback.current();
    }
  }, [state]);

  const processOptIn = useCallback(
    (callback: ProcessCallback) => {
      if (stateRef.current === OptInState.Initialized) {
        setState(OptInState.WaitForInput);
        unifiedLoggerClient.logImpressionEvent({
          eventName: DocsAssistantEvent.OptInMessage,
          parameters: {
            locale: locale ? String(locale) : '',
          },
        });
        processCallback.current = callback;
      }
    },
    [locale],
  );

  const sentPromptPending = useCallback(() => {
    setState(OptInState.SentPromptPending);
  }, [setState]);

  const decline = useCallback(() => {
    setState(OptInState.DeclinedLoading);
    unifiedLoggerClient.logClickEvent({
      eventName: DocsAssistantEvent.OptInDeclined,
      parameters: {
        locale: locale ? String(locale) : '',
      },
    });
    setTimeout(() => {
      setState(OptInState.Declined);
    }, 1000);
  }, [locale]);

  const accept = useCallback(async () => {
    setState(OptInState.AcceptedLoading);
    unifiedLoggerClient.logClickEvent({
      eventName: DocsAssistantEvent.OptInAccepted,
      parameters: {
        locale: locale ? String(locale) : '',
      },
    });
    try {
      await userAgreementsClient.acceptAgreements(agreementIds);
      setState(OptInState.Accepted);
      // call the callback in useEffect above to ensure state has settled before calling
    } catch (e) {
      unifiedLoggerClient.logErrorEvent({
        eventName: DocsAssistantEvent.OptInAcceptAgreementInternalError,
        parameters: {
          message: `${e}`,
          locale: locale ? String(locale) : '',
        },
      });
      setState(OptInState.FailedToAccept);
      throwError(
        `Internal error on AssistantOptInProvider when attempting to accept agreements ${e}`,
      );
    }
  }, [locale, agreementIds]);

  const initialize = useCallback(async () => {
    try {
      const agreements = await userAgreementsClient.getAssistantAndPrivacyPolicyAgreements();
      if (agreements.length === 0) {
        setState(OptInState.AcceptedWithoutProcess);
      } else {
        setAgreementIds(agreements.map((agreement) => agreement.id));
        setState(OptInState.Initialized);
      }
    } catch (e) {
      unifiedLoggerClient.logErrorEvent({
        eventName: DocsAssistantEvent.OptInGetAgreementInternalError,
        parameters: {
          message: `${e}`,
          locale: locale ? String(locale) : '',
        },
      });
      setState(OptInState.FailedToInitialize);
    }
  }, [locale]);

  const contextValue = useMemo(() => {
    const processing = [
      OptInState.WaitForInput,
      OptInState.SentPromptPending,
      OptInState.AcceptedLoading,
      OptInState.FailedToAccept,
      OptInState.DeclinedLoading,
      OptInState.Declined,
    ].includes(state);
    return {
      state,
      decline,
      accept,
      processing,
      accepted: state === OptInState.Accepted || state === OptInState.AcceptedWithoutProcess,
      process: processOptIn,
      initialize,
      sentPromptPending,
      initialized: state !== OptInState.Preinitialized,
    };
  }, [state, decline, accept, processOptIn, initialize, sentPromptPending]);

  return (
    <AssistantOptInContext.Provider value={contextValue}>{children}</AssistantOptInContext.Provider>
  );
};

export const useAssistantOptIn = () => {
  return useContext(AssistantOptInContext);
};

export default AssistantOptInProvider;
